import React, { useState, useEffect } from "react";
import axios from "axios";
import { Container, Row, Col, Card, ProgressBar, Table, Badge, Form } from "react-bootstrap";
import { Bar } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import CustomButton from '../../components/button';
import { toast, ToastContainer } from 'react-toastify';
import DateFilter from "../../components/datefilter/index";
import Filters from "../../components/date-and-vendor-filter/index";
import SpinnerComp from "../../components/spinner/index";

Chart.register(...registerables);

const Dashboard = (user) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedVendorId, setSelectedVendorId] = useState("");

  const [riders, setRiders] = useState([]);
  const [selectedRider, setSelectedRider] = useState(null);
  const [selectedRiderId, setSelectedRiderId] = useState("");

  
  const fetchDashboardData = async (from = "", to = "", vendorId="", riderId='') => {
    try {
      
      
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_DEV_BACKEND_URL}/shipment/dashboard`,
        {
          fromDate: from || null,
          toDate: to || null,
          vendorId: vendorId || null,
          riderId: riderId || null
        },
        {
          headers: {
            Authorization: `Bearer ${user.user.token}`,
          },
        }
      );
      
      setDashboardData(response.data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError("Failed to fetch dashboard data. Please try again later.");
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_DEV_BACKEND_URL}/users/all-user`,
        {
          headers: {
            Authorization: `Bearer ${user.user.token}`,
          },
          params: { status: "active", role: "vendor" },

        }
      );
      
      setVendors(response.data.sellers.map((sellers) => ({ value: sellers._id, label: sellers.name })));
      
    } catch (err) {
      console.error("Error fetching vendors:", err);
    }
  };


  const fetchRiders = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_DEV_BACKEND_URL}/users/all-user`,
        {
          headers: {
            Authorization: `Bearer ${user.user.token}`,
          },
          params: { status: "active", role: "rider" },
        }
      );
      
      setRiders(
        response.data.sellers.map((rider) => ({
          value: rider._id,
          label: rider.name,
        }))
      );
    } catch (err) {
      console.error("Error fetching riders:", err);
    }
  };


  useEffect(() => {
    fetchDashboardData();
    if (user.user.role === "admin") {
      fetchVendors();
      fetchRiders()
    }
  }, []);

  const handleFilter = () => {
    if (fromDate && !toDate) {
      toast.error("To Date is missing.")
      return;
    }if (!fromDate && toDate) {
      toast.error("From Date is missing.")
      return;
    }
    
    if (fromDate && toDate && new Date(toDate) < new Date(fromDate)) {
      toast.error("To Date cannot be earlier than From Date.");
      return;
    }
    
    if (toDate && new Date().toLocaleString('en-GB', { timeZone: 'Asia/Dubai' }) < new Date(toDate)) {
      toast.error("To Date cannot be later than the current Date.");
      return;
    }
    
    fetchDashboardData(fromDate, toDate, selectedVendorId, selectedRiderId);
  };

  const handleReset = () => {
    setSelectedVendor("")
    setSelectedVendorId(null)
    setSelectedRider("")
    setSelectedRiderId(null)
    setFromDate("");
    setToDate("");
    fetchDashboardData(); // Fetch with default dates
  };

  if (loading) return <SpinnerComp />;


  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  const {
    totalShipments,
    pendingShipments,
    completedShipments,
    cancelledShipments,
    pendingDetails,
  } = dashboardData;

  const graphData = {
    labels: [
      "Total",
      "Completed",
      "Pending (In Transit)",
      "Pending (Rescheduled)",
      "Pending (Inactive)",
      "Cancelled",
    ],
    datasets: [
      {
        label: "Number of Shipments",
        data: [
          totalShipments,
          completedShipments,
          pendingDetails.inTransit,
          pendingDetails.rescheduled,
          pendingDetails.inactive,
          cancelledShipments,
        ],
        backgroundColor: [
          "#90AFC5",
          "#679560",
          "#408EC6",
          "#6c757d",
          "#ff7f0e",
          "#7A2048",
        ],
      },
    ],
  };

  const graphOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => `Shipments: ${context.raw}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 10,
        },
      },
    },
  };

  return (
    <Container fluid className="p-4 bg-light">
      <h2 className="mb-2 mt-1 fw-bold" style={{ color: '#005A9C' }}>Dashboard</h2>
    

      <br />
      <ToastContainer />
      {user.user.role==="admin"?       
      <Filters
        fromDate={fromDate}
        toDate={toDate}
        setFromDate={setFromDate}
        setToDate={setToDate}
        vendors={vendors}
        selectedVendor={selectedVendor}
        setSelectedVendor={setSelectedVendor}
        selectedVendorId={selectedVendorId}
        setSelectedVendorId={setSelectedVendorId}

        riders={riders}
        selectedRider={selectedRider}
        setSelectedRider={setSelectedRider}
        selectedRiderId={selectedRiderId}
        setSelectedRiderId={setSelectedRiderId}


        handleFilter={handleFilter}
        handleReset={handleReset}
        isAdmin={user.user.role === "admin"}
      />
      :      
      <DateFilter
        fromDate={fromDate}
        toDate={toDate}
        setFromDate={setFromDate}
        setToDate={setToDate}
        handleFilter={handleFilter}
        handleReset={handleReset}
      />}
      <Row className="mb-4 g-4">
        <Col md={3}>
          <Card className="shadow-sm text-center">
            <Card.Body>
              <h5 style={{ color: "#90AFC5" }}>Total Shipments</h5>
              <h2 className="display-4" style={{ color: "#90AFC5" }}>{totalShipments}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm text-center">
            <Card.Body>
              <h5 style={{ color: "#408EC6" }}>Pending Shipments</h5>
              <h2 className="display-4" style={{ color: "#408EC6" }}>{pendingShipments}</h2>
              <ProgressBar className="mt-3">
                <ProgressBar
                  striped
                  variant="info"
                  now={(pendingDetails.inTransit / pendingShipments) * 100}
                  label="In Transit"
                  key={1}
                />
                <ProgressBar
                  striped
                  variant="secondary"
                  now={(pendingDetails.rescheduled / pendingShipments) * 100}
                  label="Rescheduled"
                  key={2}
                />
                <ProgressBar
                  striped
                  variant="warning"
                  now={(pendingDetails.inactive / pendingShipments) * 100}
                  label="Inactive"
                  key={3}
                />
              </ProgressBar>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm text-center">
            <Card.Body>
              <h5 style={{ color: "#679560" }}>Completed Shipments</h5>
              <h2 className="display-4" style={{ color: "#679560" }}>{completedShipments}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm text-center">
            <Card.Body>
              <h5 style={{ color: "#7A2048" }}>Cancelled Shipments</h5>
              <h2 className="display-4" style={{ color: "#7A2048" }}>{cancelledShipments}</h2>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm border-info">
            <Card.Body>
              <h5 className="text-info">Pending Shipment Details</h5>
              <Table striped bordered hover className="mt-3">
                <thead>
                  <tr className="bg-light text-dark">
                    <th>Status</th>
                    <th>Count</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <Badge bg="info">In Transit</Badge>
                    </td>
                    <td>{pendingDetails.inTransit}</td>
                    <td>Shipments currently on the way.</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge bg="secondary">Rescheduled</Badge>
                    </td>
                    <td>{pendingDetails.rescheduled}</td>
                    <td>Shipments delayed and rescheduled.</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge bg="warning">Inactive</Badge>
                    </td>
                    <td>{pendingDetails.inactive}</td>
                    <td>Shipments that are inactive and need attention.</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Body>
              <h5>Graphical View</h5>
              <div style={{ height: "500px" }}>
                <Bar data={graphData} options={graphOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;
